import { apiClient } from 'services/api/apiClient';
import type { ServiceVersion } from 'models/ServiceVersion';

/**
 * 현장별·일자별 상태 보고. Teams 로 나간 그 현장 단락을 그대로 담는다 —
 * 백엔드(site-health)의 SiteHealthSiteReport 와 같은 모양이다.
 */
export interface SiteHealthReport {
  id: number;
  project_id: number;
  date: string;
  code: string;
  name_ko: string;
  /** cron: 매일 아침 보고 · manual: 전체 수동 실행 · site: 이 현장만 점검 */
  trigger: 'cron' | 'manual' | 'site';
  grade: 'ok' | 'warning' | 'critical';
  markdown: string;
  collected_at: string;
}

/** 현장 목록 서버 상태 칸 — 10분마다 통합서버·영상처리서버 컨테이너 상태를 본 마지막 결과. */
export interface SiteLiveNode {
  name: string;
  ok: boolean;
  reason?: string;
}

export interface SiteLiveStatus {
  project_id: number;
  code: string;
  name_ko: string;
  /** 방재실 이름 — 방재실이 여러 곳인 현장은 같은 project_id 로 여러 개가 온다. */
  group?: string;
  checked_at: string;
  server_ok: boolean;
  server_reason?: string;
  /** 통합서버 기대 서비스 수 / 그중 정상인 수. 컨테이너 목록을 못 읽었으면 0. */
  services?: number;
  services_ok?: number;
  nodes: SiteLiveNode[];
  nodes_ok: number;
  /** NVR — 통합서버에서 핑으로 본 결과. */
  nvrs?: SiteLiveNode[];
  nvrs_ok?: number;
  /** 통합서버에서 서비스별로 떠 있는 이미지 버전. 통합서버를 못 읽었으면 없다. */
  versions?: ServiceVersion[];
  /** 서비스가 지금 실제로 읽는 설정 파일(원문 제외 — liveConfig 로 연다). 못 읽었으면 없다. */
  configs?: SiteLiveConfigFile[];
}

/** 현장 서비스 하나가 읽는 설정 파일 하나 — 저장소 파일(docker/deploy/sites/<코드>/<file>)과 sha 로 비교한다. */
export interface SiteLiveConfigFile {
  /** 저장소 파일 이름(router.env …) */
  file: string;
  /** 서비스(appname) */
  app: string;
  /** 컨테이너 안 경로 — no_var 면 비어 있는 환경변수 이름($NODE_ENV) */
  path?: string;
  /** ok · no_container(컨테이너 없음) · no_var(현장 이름 환경변수 없음) · missing(파일 없음) */
  state: 'ok' | 'no_container' | 'no_var' | 'missing';
  /** git blob sha — 저장소 파일 sha 와 같으면 내용이 같다 */
  sha?: string;
  size: number;
  /** 파일 값 대신 컨테이너 환경변수 값이 쓰이는 키 */
  env_overrides?: string[];
}

export interface SiteLiveConfigContent extends SiteLiveConfigFile {
  content: string;
  checked_at: string;
}

export interface SiteLiveResponse {
  /** false 면 이 서버에서 현장 점검이 꺼져 있다 (SITE_HEALTH_ENABLED=false). */
  enabled: boolean;
  interval_sec?: number;
  last_run_at?: string | null;
  data: SiteLiveStatus[];
}

export const SiteHealthService = {
  async live(signal?: AbortSignal): Promise<SiteLiveResponse> {
    return (await apiClient.get<SiteLiveResponse>('/v0.1/site-health/live', { signal })).data;
  },

  /** 다음 주기(10분)를 기다리지 않고 전 현장을 다시 점검하게 한다 — 결과는 live() 로 읽는다. */
  async refreshLive(): Promise<void> {
    await apiClient.post('/v0.1/site-health/live/refresh');
  },

  /** 마지막 점검에서 읽은 현장 실제 설정 파일 원문 (관리자·편집자). group 은 방재실, 하나면 ''. */
  async liveConfig(projectId: number, group: string, app: string, file: string): Promise<SiteLiveConfigContent> {
    const res = await apiClient.get<{ data: SiteLiveConfigContent }>('/v0.1/site-health/live/config', {
      params: { project_id: projectId, group, app, file },
    });
    return res.data.data;
  },

  async reports(
    siteId: number,
    signal?: AbortSignal,
  ): Promise<SiteHealthReport[]> {
    return (
      await apiClient.get<SiteHealthReport[]>(
        `/v0.1/site-health/sites/${siteId}/reports`,
        { signal },
      )
    ).data;
  },
};
